import React, { useCallback, useEffect, useMemo, useState } from "react";
import { adminApi, resolveImage } from "../../lib/admin-api";
import { Edit2, X, RefreshCw, Upload, Shuffle, Plus, Trash2, Sparkles } from "lucide-react";
import RoomImagePreviewModal from "./RoomImagePreviewModal";

const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

export default function AdminRooms() {
  const [rooms, setRooms] = useState([]);
  const [classics, setClassics] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    const [r, c] = await Promise.all([
      adminApi.get("/rooms"),
      adminApi.get("/classics", { params: { limit: 200 } }),
    ]);
    setRooms(r.data.items || []);
    setClassics(c.data.items || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-rooms" className="max-w-5xl">
      <div className="text-xs uppercase tracking-[0.3em] text-stoel-walnut mb-3">— Inhoud</div>
      <div className="flex items-end justify-between mb-10">
        <h1 className="stoel-display text-4xl">Ruimtes</h1>
        <button
          data-testid="room-create-open"
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-stoel-ink text-stoel-paper text-sm hover:bg-stoel-terracotta"
        >
          <Plus size={14} /> Nieuwe ruimte
        </button>
      </div>

      <div className="space-y-4">
        {rooms.map((r) => (
          <div key={r.slug} data-testid={`room-row-${r.slug}`} className="bg-stoel-paper border border-stoel-line p-5 flex gap-5 items-center">
            <img src={resolveImage(r.hero_image_url)} alt="" className="w-32 h-20 object-cover bg-stoel-paperDeep" />
            <div className="flex-1">
              <div className="text-lg font-light text-stoel-ink">{r.title_nl}</div>
              <div className="text-xs text-stoel-inkSoft mt-1 line-clamp-1">{r.intro_nl}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-stoel-walnut mt-1">{(r.featured_classic_ids || []).length} stukken · {r.slug}</div>
            </div>
            <button data-testid={`edit-room-${r.slug}`} onClick={() => setEditing(r)} className="text-stoel-ink hover:text-stoel-terracotta p-2"><Edit2 size={16} /></button>
          </div>
        ))}
      </div>

      {editing && (
        <RoomEditDrawer
          room={editing}
          classics={classics}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          onDeleted={() => { setEditing(null); load(); }}
        />
      )}
      {creating && (
        <RoomCreateDrawer
          classics={classics}
          existingSlugs={rooms.map((r) => r.slug)}
          nextOrder={rooms.length + 1}
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); load(); }}
        />
      )}
    </div>
  );
}


function RoomEditDrawer({ room, classics, onClose, onSaved, onDeleted }) {
  const [form, setForm] = useState({
    title_nl: room.title_nl || "",
    title_en: room.title_en || "",
    intro_nl: room.intro_nl || "",
    intro_en: room.intro_en || "",
    hero_image_url: room.hero_image_url || "",
    featured_classic_ids: room.featured_classic_ids || [],
  });
  const [altUrl, setAltUrl] = useState(room.hero_image_url_alt || "");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const swapHero = async () => {
    setImgBusy(true); setError("");
    try {
      const r = await adminApi.post(`/rooms/${room.slug}/swap-hero`);
      set("hero_image_url", r.data.hero_image_url);
      setAltUrl(r.data.hero_image_url_alt || "");
    } catch (err) { setError(err?.response?.data?.detail || "Wisselen mislukt"); }
    finally { setImgBusy(false); }
  };

  const removeRoom = async () => {
    setDeleting(true); setError("");
    try {
      await adminApi.delete(`/rooms/${room.slug}`);
      onDeleted();
    } catch (err) {
      setError(err?.response?.data?.detail || "Verwijderen mislukt");
      setDeleting(false);
    }
  };

  const filteredClassics = classics.filter((c) =>
    !search ||
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.designer.toLowerCase().includes(search.toLowerCase())
  ).slice(0, 30);

  const toggleClassic = (id) => {
    set("featured_classic_ids",
      form.featured_classic_ids.includes(id)
        ? form.featured_classic_ids.filter((x) => x !== id)
        : [...form.featured_classic_ids, id]
    );
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImgBusy(true); setError("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await adminApi.post("/images/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("hero_image_url", r.data.url);
    } catch (err) { setError(err?.response?.data?.detail || "Upload mislukt"); }
    finally { setImgBusy(false); }
  };

  const regen = async () => {
    setImgBusy(true); setError("");
    try {
      const ctx = `the room "${form.title_en || form.title_nl}" — ${form.intro_en || form.intro_nl}`;
      const r = await adminApi.post("/images/generate", { prompt_context: ctx, kind: "room", out_slug: `${room.slug}_${Date.now()}` });
      set("hero_image_url", r.data.url);
    } catch (err) { setError(err?.response?.data?.detail || "Beeld genereren mislukt"); }
    finally { setImgBusy(false); }
  };

  const save = async () => {
    setSaving(true); setError("");
    try {
      await adminApi.patch(`/rooms/${room.slug}`, form);
      onSaved();
    } catch (err) { setError(err?.response?.data?.detail || "Opslaan mislukt"); }
    finally { setSaving(false); }
  };

  const hasClassics = form.featured_classic_ids.length > 0;
  const canAi = hasClassics && (form.title_nl.trim() || form.title_en.trim()) && (form.intro_nl.trim() || form.intro_en.trim());
  const selectedClassics = useMemo(
    () => form.featured_classic_ids.map((id) => classics.find((c) => c.id === id)).filter(Boolean),
    [form.featured_classic_ids, classics],
  );

  const handleAiSave = useCallback(async (preview_id /* , preview_url */) => {
    const r = await adminApi.post(`/rooms/${room.slug}/save-generated-image`, { preview_id });
    set("hero_image_url", r.data.hero_image_url);
    setAltUrl(r.data.hero_image_url_alt || "");
    setAiModalOpen(false);
  }, [room.slug]);

  return (
    <div data-testid="room-edit-drawer" className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-stoel-ink/50" onClick={onClose} />
      <div className="w-full max-w-2xl bg-stoel-paper overflow-y-auto p-8 md:p-10 shadow-2xl">
        <div className="flex items-start justify-between mb-6">
          <h2 className="stoel-display text-3xl">Ruimte: {room.slug}</h2>
          <button onClick={onClose} className="text-stoel-inkSoft hover:text-stoel-ink"><X /></button>
        </div>

        <div className="space-y-5">
          {/* STAP 1 — Meubels (eerst) */}
          <div>
            <div className="stoel-eyebrow text-stoel-walnut mb-2">Stap 1 — Meubels</div>
            <Field label={`Geselecteerde stukken (${form.featured_classic_ids.length})`}>
              {!hasClassics && (
                <div className="text-xs text-stoel-terracotta mb-2">Kies minstens 2 stukken voor een rijk gegenereerd ruimtebeeld.</div>
              )}
              {selectedClassics.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2 p-2 bg-stoel-paperDeep/30 border border-stoel-line/40">
                  {selectedClassics.map((c) => (
                    <div key={c.id} className="flex items-center gap-2 bg-stoel-paper border border-stoel-line pr-2">
                      {c.image_url ? (
                        <img src={resolveImage(c.image_url)} alt="" className="w-10 h-10 object-cover" />
                      ) : (
                        <div className="w-10 h-10 bg-stoel-paperDeep" />
                      )}
                      <div>
                        <div className="text-xs text-stoel-ink line-clamp-1 max-w-[120px]">{c.name}</div>
                        <div className="text-[10px] text-stoel-walnut line-clamp-1 max-w-[120px]">{c.designer}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleClassic(c.id)}
                        className="text-stoel-walnut hover:text-stoel-terracotta ml-1"
                        aria-label={`Verwijder ${c.name}`}
                      ><X size={12} /></button>
                    </div>
                  ))}
                </div>
              )}
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Zoek stukken om toe te voegen…" className={inputCls + " mb-2"} />
              <div className="max-h-56 overflow-y-auto border border-stoel-line bg-stoel-paperDeep/30 divide-y divide-stoel-line/40">
                {filteredClassics.map((c) => {
                  const on = form.featured_classic_ids.includes(c.id);
                  return (
                    <label key={c.id} className={`flex items-center gap-3 px-3 py-2 cursor-pointer text-sm ${on ? "bg-stoel-ink/5" : ""}`}>
                      <input type="checkbox" checked={on} onChange={() => toggleClassic(c.id)} data-testid={`room-classic-${c.slug}`} />
                      <span className="flex-1">{c.name}</span>
                      <span className="text-xs text-stoel-walnut">{c.designer}</span>
                    </label>
                  );
                })}
              </div>
            </Field>
          </div>

          {/* STAP 2 — Ruimte info */}
          <div>
            <div className="stoel-eyebrow text-stoel-walnut mb-2">Stap 2 — Ruimte-info</div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Title NL"><input data-testid="room-title-nl" value={form.title_nl} onChange={(e) => set("title_nl", e.target.value)} className={inputCls} /></Field>
              <Field label="Title EN"><input data-testid="room-title-en" value={form.title_en} onChange={(e) => set("title_en", e.target.value)} className={inputCls} /></Field>
            </div>
            <div className="mt-3 grid grid-cols-1 gap-3">
              <Field label="Intro NL"><textarea data-testid="room-intro-nl" rows={3} value={form.intro_nl} onChange={(e) => set("intro_nl", e.target.value)} className={inputCls} /></Field>
              <Field label="Intro EN"><textarea data-testid="room-intro-en" rows={3} value={form.intro_en} onChange={(e) => set("intro_en", e.target.value)} className={inputCls} /></Field>
            </div>
          </div>

          {/* STAP 3 — Ruimtebeeld */}
          <div>
            <div className="stoel-eyebrow text-stoel-walnut mb-2">Stap 3 — Ruimtebeeld</div>
            <Field label="Hero beeld">
              <div className="flex items-start gap-3">
                {form.hero_image_url && (
                  <div className="flex flex-col items-center gap-1">
                    <img src={resolveImage(form.hero_image_url)} alt="" className="w-32 h-20 object-cover bg-stoel-paperDeep border border-stoel-line" />
                    <span className="text-[10px] uppercase tracking-[0.2em] text-stoel-walnut">Huidig</span>
                  </div>
                )}
                <div className="flex flex-col gap-2 flex-1">
                  <button
                    type="button"
                    data-testid="room-ai-generate"
                    onClick={() => setAiModalOpen(true)}
                    disabled={!canAi || imgBusy}
                    className="inline-flex items-center gap-2 px-3 py-2 bg-stoel-terracotta text-stoel-paper text-sm hover:bg-stoel-ink disabled:opacity-50 disabled:cursor-not-allowed"
                    title={canAi ? "" : "Selecteer meubels + vul title_nl/en en intro_nl/en in om te activeren"}
                  >
                    <Sparkles size={14} /> Genereer ruimtebeeld met AI
                  </button>
                  <label className="inline-flex items-center gap-2 px-3 py-2 border border-stoel-ink text-sm cursor-pointer hover:bg-stoel-ink hover:text-stoel-paper">
                    <Upload size={14} /> Upload eigen bestand
                    <input type="file" accept="image/*" hidden onChange={upload} />
                  </label>
                  <button data-testid="room-regen-image" onClick={regen} disabled={imgBusy} className="inline-flex items-center gap-2 px-3 py-2 border border-stoel-line text-sm hover:bg-stoel-paperDeep disabled:opacity-50">
                    <RefreshCw size={14} /> {imgBusy ? "Bezig…" : "Snelle AI-regenereer (zonder meubelcontext)"}
                  </button>
                  {altUrl && (
                    <button
                      data-testid={`room-swap-alt-${room.slug}`}
                      onClick={swapHero}
                      disabled={imgBusy}
                      className="inline-flex items-center gap-2 px-3 py-2 border border-stoel-terracotta text-stoel-terracotta text-sm hover:bg-stoel-terracotta hover:text-stoel-paper disabled:opacity-50"
                    >
                      <Shuffle size={14} /> Wissel naar variant B
                    </button>
                  )}
                </div>
                {altUrl && (
                  <div className="flex flex-col items-center gap-1">
                    <img
                      src={resolveImage(altUrl)}
                      alt="Alternate"
                      className="w-20 h-12 object-cover bg-stoel-paperDeep border border-dashed border-stoel-walnut opacity-70"
                    />
                    <span className="text-[10px] uppercase tracking-[0.2em] text-stoel-walnut">Alt</span>
                  </div>
                )}
              </div>
            </Field>
          </div>

          {error && <div className="bg-stoel-terracotta/10 border-l-2 border-stoel-terracotta p-3 text-sm">{error}</div>}

          <div className="pt-4 flex gap-3 items-center">
            <button data-testid="room-save" onClick={save} disabled={saving} className="px-6 py-2.5 bg-stoel-ink text-stoel-paper text-sm hover:bg-stoel-terracotta disabled:opacity-50">
              {saving ? "Opslaan…" : "Opslaan"}
            </button>
            <button onClick={onClose} className="px-5 py-2.5 border border-stoel-line text-sm hover:bg-stoel-paperDeep">Annuleren</button>
            <div className="flex-1" />
            {!confirmDelete ? (
              <button
                data-testid={`room-delete-${room.slug}`}
                onClick={() => setConfirmDelete(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-stoel-terracotta hover:bg-stoel-terracotta/10"
              >
                <Trash2 size={14} /> Verwijder ruimte
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-xs text-stoel-inkSoft">Zeker weten?</span>
                <button
                  data-testid={`room-delete-confirm-${room.slug}`}
                  onClick={removeRoom}
                  disabled={deleting}
                  className="px-3 py-1.5 bg-stoel-terracotta text-stoel-paper text-sm hover:bg-stoel-ink disabled:opacity-50"
                >
                  {deleting ? "Verwijderen…" : "Ja, verwijder"}
                </button>
                <button
                  onClick={() => setConfirmDelete(false)}
                  className="px-3 py-1.5 border border-stoel-line text-sm hover:bg-stoel-paperDeep"
                >
                  Annuleer
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {aiModalOpen && (
        <RoomImagePreviewModal
          open={aiModalOpen}
          roomInput={form}
          classicIds={form.featured_classic_ids}
          classicsLookup={selectedClassics}
          currentHeroUrl={form.hero_image_url}
          onSave={handleAiSave}
          onClose={() => setAiModalOpen(false)}
        />
      )}
    </div>
  );
}

function RoomCreateDrawer({ classics, existingSlugs, nextOrder, onClose, onSaved }) {
  const [form, setForm] = useState({
    slug: "",
    title_nl: "",
    title_en: "",
    intro_nl: "",
    intro_en: "",
    hero_image_url: "",  // shown when uploaded directly (no preview_id)
    featured_classic_ids: [],
    order: nextOrder,
    preview_id: "",       // if set, server promotes /static/previews/preview_<id>.png on create
    preview_url: "",      // local-only, for showing the preview thumb until create
  });
  const [slugTouched, setSlugTouched] = useState(false);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onTitleChange = (v) => {
    set("title_nl", v);
    if (!slugTouched) set("slug", slugify(v));
  };

  const slugConflict = !!form.slug && existingSlugs.includes(form.slug);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImgBusy(true); setError("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await adminApi.post("/images/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // uploaded asset is permanent, so clear any pending preview_id
      setForm((f) => ({ ...f, hero_image_url: r.data.url, preview_id: "", preview_url: "" }));
    } catch (err) { setError(err?.response?.data?.detail || "Upload mislukt"); }
    finally { setImgBusy(false); }
  };

  const quickRegen = async () => {
    if (!(form.title_en || form.title_nl)) {
      setError("Geef eerst een titel op voor AI-generatie");
      return;
    }
    setImgBusy(true); setError("");
    try {
      const ctx = `the room "${form.title_en || form.title_nl}" — ${form.intro_en || form.intro_nl}`;
      const r = await adminApi.post("/images/generate", { prompt_context: ctx, kind: "room", out_slug: `${form.slug || "newroom"}_${Date.now()}` });
      setForm((f) => ({ ...f, hero_image_url: r.data.url, preview_id: "", preview_url: "" }));
    } catch (err) { setError(err?.response?.data?.detail || "Beeld genereren mislukt"); }
    finally { setImgBusy(false); }
  };

  const toggleClassic = (id) => {
    set("featured_classic_ids",
      form.featured_classic_ids.includes(id)
        ? form.featured_classic_ids.filter((x) => x !== id)
        : [...form.featured_classic_ids, id]
    );
  };

  const filteredClassics = classics.filter((c) =>
    !search ||
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.designer.toLowerCase().includes(search.toLowerCase())
  ).slice(0, 30);

  const selectedClassics = useMemo(
    () => form.featured_classic_ids.map((id) => classics.find((c) => c.id === id)).filter(Boolean),
    [form.featured_classic_ids, classics],
  );

  const hasClassics = form.featured_classic_ids.length > 0;
  const canAi = hasClassics && (form.title_nl.trim() || form.title_en.trim()) && (form.intro_nl.trim() || form.intro_en.trim());

  const handleAiSave = useCallback(async (preview_id, preview_url) => {
    // For new rooms we stash preview_id in form and commit on Save.
    setForm((f) => ({ ...f, preview_id, preview_url, hero_image_url: "" }));
    setAiModalOpen(false);
  }, []);

  const valid =
    form.slug && !slugConflict &&
    form.title_nl.trim() && form.title_en.trim();

  const save = async () => {
    if (!valid) {
      setError("Slug, Title NL en Title EN zijn verplicht");
      return;
    }
    setSaving(true); setError("");
    try {
      const payload = { ...form };
      delete payload.preview_url;  // local-only
      if (!payload.preview_id) delete payload.preview_id;
      await adminApi.post(`/rooms`, payload);
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || "Aanmaken mislukt");
      setSaving(false);
    }
  };

  return (
    <div data-testid="room-create-drawer" className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-stoel-ink/50" onClick={onClose} />
      <div className="w-full max-w-2xl bg-stoel-paper overflow-y-auto p-8 md:p-10 shadow-2xl">
        <div className="flex items-start justify-between mb-6">
          <h2 className="stoel-display text-3xl">Nieuwe ruimte</h2>
          <button onClick={onClose} className="text-stoel-inkSoft hover:text-stoel-ink"><X /></button>
        </div>

        <div className="space-y-5">
          {/* STAP 1 — Meubels eerst */}
          <div>
            <div className="stoel-eyebrow text-stoel-walnut mb-2">Stap 1 — Meubels</div>
            <Field label={`Geselecteerde stukken (${form.featured_classic_ids.length})`}>
              {!hasClassics && (
                <div className="text-xs text-stoel-terracotta mb-2">Kies minstens 2 stukken voor een rijk gegenereerd ruimtebeeld.</div>
              )}
              {selectedClassics.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2 p-2 bg-stoel-paperDeep/30 border border-stoel-line/40">
                  {selectedClassics.map((c) => (
                    <div key={c.id} className="flex items-center gap-2 bg-stoel-paper border border-stoel-line pr-2">
                      {c.image_url ? <img src={resolveImage(c.image_url)} alt="" className="w-10 h-10 object-cover" /> : <div className="w-10 h-10 bg-stoel-paperDeep" />}
                      <div>
                        <div className="text-xs text-stoel-ink line-clamp-1 max-w-[120px]">{c.name}</div>
                        <div className="text-[10px] text-stoel-walnut line-clamp-1 max-w-[120px]">{c.designer}</div>
                      </div>
                      <button type="button" onClick={() => toggleClassic(c.id)} className="text-stoel-walnut hover:text-stoel-terracotta ml-1" aria-label={`Verwijder ${c.name}`}>
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Zoek stukken om toe te voegen…" className={inputCls + " mb-2"} />
              <div className="max-h-56 overflow-y-auto border border-stoel-line bg-stoel-paperDeep/30 divide-y divide-stoel-line/40">
                {filteredClassics.map((c) => {
                  const on = form.featured_classic_ids.includes(c.id);
                  return (
                    <label key={c.id} className={`flex items-center gap-3 px-3 py-2 cursor-pointer text-sm ${on ? "bg-stoel-ink/5" : ""}`}>
                      <input type="checkbox" checked={on} onChange={() => toggleClassic(c.id)} data-testid={`new-room-classic-${c.slug}`} />
                      <span className="flex-1">{c.name}</span>
                      <span className="text-xs text-stoel-walnut">{c.designer}</span>
                    </label>
                  );
                })}
              </div>
            </Field>
          </div>

          {/* STAP 2 — Ruimte info */}
          <div>
            <div className="stoel-eyebrow text-stoel-walnut mb-2">Stap 2 — Ruimte-info</div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Title NL">
                <input data-testid="new-room-title-nl" value={form.title_nl} onChange={(e) => onTitleChange(e.target.value)} className={inputCls} placeholder="bv. Lobby" />
              </Field>
              <Field label="Title EN">
                <input data-testid="new-room-title-en" value={form.title_en} onChange={(e) => set("title_en", e.target.value)} className={inputCls} placeholder="e.g. Lobby" />
              </Field>
            </div>
            <div className="mt-3">
              <Field label={`Slug (kebab-case)${slugConflict ? " — bestaat al" : ""}`}>
                <input
                  data-testid="new-room-slug"
                  value={form.slug}
                  onChange={(e) => { setSlugTouched(true); set("slug", slugify(e.target.value)); }}
                  className={`${inputCls} ${slugConflict ? "border-stoel-terracotta" : ""}`}
                  placeholder="bv. lobby"
                />
              </Field>
            </div>
            <div className="mt-3 grid grid-cols-1 gap-3">
              <Field label="Intro NL"><textarea data-testid="new-room-intro-nl" rows={3} value={form.intro_nl} onChange={(e) => set("intro_nl", e.target.value)} className={inputCls} /></Field>
              <Field label="Intro EN"><textarea data-testid="new-room-intro-en" rows={3} value={form.intro_en} onChange={(e) => set("intro_en", e.target.value)} className={inputCls} /></Field>
            </div>
          </div>

          {/* STAP 3 — Ruimtebeeld */}
          <div>
            <div className="stoel-eyebrow text-stoel-walnut mb-2">Stap 3 — Ruimtebeeld</div>
            <Field label="Hero beeld">
              <div className="flex items-start gap-3">
                {form.preview_url ? (
                  <div className="flex flex-col items-center gap-1">
                    <img src={resolveImage(form.preview_url)} alt="" className="w-32 h-20 object-cover bg-stoel-paperDeep border border-stoel-terracotta" />
                    <span className="text-[10px] uppercase tracking-[0.2em] text-stoel-terracotta">Nieuw</span>
                  </div>
                ) : form.hero_image_url ? (
                  <img src={resolveImage(form.hero_image_url)} alt="" className="w-32 h-20 object-cover bg-stoel-paperDeep border border-stoel-line" />
                ) : (
                  <div className="w-32 h-20 bg-stoel-paperDeep border border-dashed border-stoel-line flex items-center justify-center text-[10px] uppercase tracking-[0.2em] text-stoel-walnut">geen</div>
                )}
                <div className="flex flex-col gap-2 flex-1">
                  <button
                    type="button"
                    data-testid="new-room-ai-generate"
                    onClick={() => setAiModalOpen(true)}
                    disabled={!canAi || imgBusy}
                    className="inline-flex items-center gap-2 px-3 py-2 bg-stoel-terracotta text-stoel-paper text-sm hover:bg-stoel-ink disabled:opacity-50 disabled:cursor-not-allowed"
                    title={canAi ? "" : "Selecteer ≥1 stuk en vul titel + intro in"}
                  >
                    <Sparkles size={14} /> Genereer ruimtebeeld met AI
                  </button>
                  <label className="inline-flex items-center gap-2 px-3 py-2 border border-stoel-ink text-sm cursor-pointer hover:bg-stoel-ink hover:text-stoel-paper">
                    <Upload size={14} /> Upload eigen bestand
                    <input type="file" accept="image/*" hidden onChange={upload} />
                  </label>
                  <button
                    data-testid="new-room-regen-image"
                    onClick={quickRegen}
                    disabled={imgBusy}
                    className="inline-flex items-center gap-2 px-3 py-2 border border-stoel-line text-sm hover:bg-stoel-paperDeep disabled:opacity-50"
                  >
                    <RefreshCw size={14} /> {imgBusy ? "Bezig…" : "Snelle AI-regenereer (zonder meubelcontext)"}
                  </button>
                </div>
              </div>
            </Field>
          </div>

          {/* STAP 4 — Volgorde */}
          <div>
            <div className="stoel-eyebrow text-stoel-walnut mb-2">Stap 4 — Plaatsing</div>
            <Field label="Volgorde">
              <input
                type="number"
                data-testid="new-room-order"
                value={form.order}
                onChange={(e) => set("order", parseInt(e.target.value || "0", 10))}
                className={`${inputCls} w-24`}
              />
            </Field>
          </div>

          {error && <div className="bg-stoel-terracotta/10 border-l-2 border-stoel-terracotta p-3 text-sm">{error}</div>}

          <div className="pt-4 flex gap-3">
            <button
              data-testid="new-room-save"
              onClick={save}
              disabled={saving || !valid}
              className="px-6 py-2.5 bg-stoel-ink text-stoel-paper text-sm hover:bg-stoel-terracotta disabled:opacity-50"
            >
              {saving ? "Aanmaken…" : "Ruimte aanmaken"}
            </button>
            <button onClick={onClose} className="px-5 py-2.5 border border-stoel-line text-sm hover:bg-stoel-paperDeep">Annuleren</button>
          </div>
        </div>
      </div>
      {aiModalOpen && (
        <RoomImagePreviewModal
          open={aiModalOpen}
          roomInput={form}
          classicIds={form.featured_classic_ids}
          classicsLookup={selectedClassics}
          currentHeroUrl={form.hero_image_url || form.preview_url}
          onSave={handleAiSave}
          onClose={() => setAiModalOpen(false)}
        />
      )}
    </div>
  );
}

const inputCls = "w-full bg-stoel-paper border border-stoel-line px-3 py-2 text-sm focus:outline-none focus:border-stoel-ink";
function Field({ label, children }) {
  return <label className="block"><span className="block text-[10px] uppercase tracking-[0.2em] text-stoel-walnut mb-1.5">{label}</span>{children}</label>;
}
